import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Modal, Table, Badge, Alert } from 'react-bootstrap';
import Calendar from 'react-calendar';
import { useAppStore } from '../../store/appStore';
import { apiService } from '../../services/api';
import { Slot } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmModal from '../common/ConfirmModal';

const DatesPage: React.FC = () => {
  const {
    dates,
    slots,
    loading,
    selectedDate,
    setDates,
    setSlots,
    setSelectedDate,
    setLoading,
  } = useAppStore();

  const [alert, setAlert] = useState<{ variant: 'success' | 'danger'; message: string } | null>(null);
  const [showSlotsModal, setShowSlotsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'date' | 'slot' | 'slotConsultant';
    id: string;
    action: 'activate' | 'deactivate';
    title: string;
    message: string;
  } | null>(null);
  const [processing, setProcessing] = useState(false);

  const loadDates = useCallback(async () => {
    setLoading('dates', true);
    try {
      const response = await apiService.getDates();
      setDates(response.data || []);
    } catch (error) {
      console.error('Failed to load dates:', error);
      setAlert({ variant: 'danger', message: 'Failed to load dates' });
    } finally {
      setLoading('dates', false);
    }
  }, [setLoading, setDates]);

  const loadSlots = useCallback(async (dateId: string) => {
    setLoading('slots', true);
    try {
      const response = await apiService.getSlots(dateId);
      setSlots(response.data || []);
    } catch (error) {
      console.error('Failed to load slots:', error);
      setAlert({ variant: 'danger', message: 'Failed to load slots' });
    } finally {
      setLoading('slots', false);
    }
  }, [setLoading, setSlots]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  useEffect(() => {
    if (selectedDate) {
      loadSlots(selectedDate._id);
    }
  }, [selectedDate, loadSlots]);

  const handleDateClick = (value: any) => {
    if (!value || Array.isArray(value)) return;
    
    const selectedDateData = dates.find(d => isSameDate(d.date, value));
    if (selectedDateData) {
      setSelectedDate(selectedDateData);
      setShowSlotsModal(true);
    }
  };

  const handleDateAction = (date: Date, action: 'activate' | 'deactivate') => {
    const dateData = dates.find(d => isSameDate(d.date, date));
    if (dateData) {
      setConfirmAction({
        type: 'date',
        id: dateData._id,
        action,
        title: `${action === 'activate' ? 'Activate' : 'Deactivate'} Date`,
        message: `Are you sure you want to ${action} this date?`,
      });
      setShowConfirmModal(true);
    }
  };

  const handleSlotAction = (slot: Slot, action: 'activate' | 'deactivate') => {
    setConfirmAction({
      type: 'slot',
      id: slot._id,
      action,
      title: `${action === 'activate' ? 'Activate' : 'Deactivate'} Slot`,
      message: `Are you sure you want to ${action} this slot?`,
    });
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    setProcessing(true);
    try {
      switch (confirmAction.type) {
        case 'date':
          if (confirmAction.action === 'activate') {
            await apiService.activateDate(confirmAction.id);
          } else {
            await apiService.inactivateDate(confirmAction.id);
          }
          // Refresh dates data from server
          await loadDates();
          // Update selectedDate if it's the same date that was modified
          if (selectedDate && selectedDate._id === confirmAction.id) {
            // Find the updated date from the refreshed dates array in the store
            const { dates: refreshedDates } = useAppStore.getState();
            const updatedDate = refreshedDates.find(d => d._id === confirmAction.id);
            if (updatedDate) {
              setSelectedDate(updatedDate);
            }
          }
          break;
        case 'slot':
          if (confirmAction.action === 'activate') {
            await apiService.activateSlot(confirmAction.id);
          } else {
            await apiService.inactivateSlot(confirmAction.id);
          }
          // Refresh slots data from server
          if (selectedDate) {
            await loadSlots(selectedDate._id);
          }
          break;
      }
      setAlert({ 
        variant: 'success', 
        message: `${confirmAction.action === 'activate' ? 'Activated' : 'Deactivated'} successfully` 
      });
    } catch (error) {
      console.error('Failed to perform action:', error);
      setAlert({ variant: 'danger', message: 'Failed to perform action' });
    } finally {
      setProcessing(false);
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  };


  const getSlotStatusBadge = (slot: Slot) => {
    if (slot.is_booked) return <Badge bg="danger">Booked</Badge>;
    if (slot.on_hold) return <Badge bg="warning">On Hold</Badge>;
    if (!slot.is_active) return <Badge bg="secondary">Inactive</Badge>;
    return <Badge bg="success">Available</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Helper function to compare dates without timezone issues
  const isSameDate = (date1: Date | string, date2: Date | string) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;

    const dateData = dates.find(d => isSameDate(d.date, date));

    if (dateData) {
      return null;
    }
    return null;
  };

  const tileDisabled = ({ date }: { date: Date }) => {
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>Dates & Slots Management</h2>
          <p className="text-muted">Manage available dates and time slots for appointments</p>
        </Col>
      </Row>

      {alert && (
        <Row className="mb-3">
          <Col>
            <Alert
              variant={alert.variant}
              onClose={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>

              {loading.dates ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center">
                  <div className="calendar-container mb-4">
                    <Calendar
                      onChange={handleDateClick}
                      value={selectedDate ? new Date(selectedDate.date) : undefined}
                      tileContent={tileContent}
                      tileDisabled={tileDisabled}
                      className="modern-calendar"
                      tileClassName={({ date, view }) => {
                        if (view !== 'month') return null;
                        const dateData = dates.find(d => isSameDate(d.date, date));
                        if (dateData) {
                          return dateData.is_active ? 'has-active-date' : 'has-inactive-date';
                        }
                        return null;
                      }}
                    />
                  </div>
                </div>
              )}

        </Col>
      </Row>

      {/* Enhanced Slots Modal */}
      <Modal 
        show={showSlotsModal} 
        onHide={() => setShowSlotsModal(false)} 
        size="xl" 
        centered
        dialogClassName="modal-dialog-scrollable"
        style={{ maxHeight: '90vh' }}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
              <div>
                <h4 className="mb-0 fw-bold">Time Slots</h4>
                <small className="text-muted">{selectedDate && formatDate(selectedDate.date)}</small>
              </div>
            </div>
            <Button
              variant={selectedDate?.is_active ? 'outline-danger' : 'outline-success'}
              size="sm"
              onClick={() => selectedDate && handleDateAction(
                new Date(selectedDate.date), 
                selectedDate.is_active ? 'deactivate' : 'activate'
              )}
              className="fw-medium"
            >
              <i className={`bi ${selectedDate?.is_active ? 'bi-pause-circle' : 'bi-play-circle'} me-2`}></i>
              {selectedDate?.is_active ? 'Deactivate Date' : 'Activate Date'}
            </Button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body 
          className="pt-0"
          style={{ 
            maxHeight: 'calc(90vh - 120px)',
            overflowY: 'auto',
            padding: '1.5rem'
          }}
        >
          {loading.slots ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
              <LoadingSpinner />
            </div>
          ) : (
            <div>
              {/* Summary */}
              {slots.length > 0 && (
                <div className="mb-4 p-3 bg-light rounded-3">
                  <div className="row text-center">
                    <div className="col-3">
                      <div className="fw-bold text-primary fs-5">
                        {slots.filter(s => s.is_active && !s.is_booked && !s.on_hold).length}
                      </div>
                      <small className="text-muted">Available</small>
                    </div>
                    <div className="col-3">
                      <div className="fw-bold text-danger fs-5">
                        {slots.filter(s => s.is_booked).length}
                      </div>
                      <small className="text-muted">Booked</small>
                    </div>
                    <div className="col-3">
                      <div className="fw-bold text-warning fs-5">
                        {slots.filter(s => s.on_hold).length}
                      </div>
                      <small className="text-muted">On Hold</small>
                    </div>
                    <div className="col-3">
                      <div className="fw-bold text-secondary fs-5">
                        {slots.filter(s => !s.is_active).length}
                      </div>
                      <small className="text-muted">Inactive</small>
                    </div>
                  </div>
                </div>
              )}

              {/* Slots Table */}
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr className="table-light">
                      <th className="border-0 py-3">
                        <i className="bi bi-clock me-2 text-primary"></i>Time
                      </th>
                      <th className="border-0 py-3">
                        <i className="bi bi-hourglass-split me-2 text-primary"></i>Duration
                      </th>
                      <th className="border-0 py-3">
                        <i className="bi bi-info-circle me-2 text-primary"></i>Status
                      </th>
                      <th className="border-0 py-3">
                        <i className="bi bi-gear me-2 text-primary"></i>Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-5">
                          <div className="text-muted">
                            <i className="bi bi-calendar-x fs-1 mb-3 d-block"></i>
                            <h5 className="mb-2">No slots available</h5>
                            <p className="mb-0">No time slots have been configured for this date.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      slots.map((slot) => (
                        <tr key={slot._id} className="align-middle">
                          <td className="py-3">
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                <i className="bi bi-clock text-primary"></i>
                              </div>
                              <div>
                                <strong className="fs-6">{slot.time_string}</strong>
                              </div>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className="badge bg-secondary bg-opacity-10 text-dark">
                              {slot.duration} minutes
                            </span>
                          </td>
                          <td className="py-3">
                            {getSlotStatusBadge(slot)}
                          </td>
                          <td className="py-3">
                            <div className="d-flex gap-2">
                              {slot.is_active ? (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleSlotAction(slot, 'deactivate')}
                                  disabled={slot.is_booked || slot.on_hold}
                                  className="fw-medium"
                                >
                                  <i className="bi bi-pause-circle me-1"></i>
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleSlotAction(slot, 'activate')}
                                  className="fw-medium"
                                >
                                  <i className="bi bi-play-circle me-1"></i>
                                  Activate
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Confirm Action Modal */}
      <ConfirmModal
        show={showConfirmModal}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.action === 'activate' ? 'Activate' : 'Deactivate'}
        variant={confirmAction?.action === 'activate' ? 'success' : 'danger'}
        onConfirm={handleConfirmAction}
        onCancel={() => setShowConfirmModal(false)}
        loading={processing}
      />
    </Container>
  );
};

export default DatesPage;
